
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Acucar() {
  const { t } = useTranslation();

  useEffect(() => {
    // Update page title and meta description
    document.title = `${t('sugar.hero.title')} - Sugar Export and Market | Lions Trade Expert`;
    
    // Add JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": t('sugar.hero.title'),
      "description": t('sugar.hero.subtitle'),
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/acucar`,
      "author": {
        "@type": "Organization",
        "name": "Lions Trade Expert"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Lions Trade Expert",
        "logo": {
          "@type": "ImageObject",
          "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/acucar`
      },
      "about": {
        "@type": "Thing",
        "name": "Brazilian Sugar Export",
        "description": "Information about Brazilian sugar export, VHP sugar, refined sugar, and global sugar market"
      },
      "keywords": "Brazilian sugar, sugar export, VHP, refined sugar, Brazil commodities, sugar market",
      "inLanguage": "en",
      "datePublished": "2024-01-15",
      "dateModified": "2024-01-15"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [t]);

  const sugarTypes = [
    { name: t('sugar.types.vhp.name'), description: t('sugar.types.vhp.description') },
    { name: t('sugar.types.refined.name'), description: t('sugar.types.refined.description') },
    { name: t('sugar.types.crystal.name'), description: t('sugar.types.crystal.description') }
  ];

  const regions = [
    { name: t('sugar.regions.sp.name'), production: t('sugar.regions.sp.production'), description: t('sugar.regions.sp.description') },
    { name: t('sugar.regions.mg.name'), production: t('sugar.regions.mg.production'), description: t('sugar.regions.mg.description') },
    { name: t('sugar.regions.go.name'), production: t('sugar.regions.go.production'), description: t('sugar.regions.go.description') }
  ];

  const destinations = [
    { country: 'Índia', percentage: '18%', flag: '🇮🇳' },
    { country: 'China', percentage: '15%', flag: '🇨🇳' },
    { country: 'Indonésia', percentage: '12%', flag: '🇮🇩' },
    { country: 'Bangladesh', percentage: '8%', flag: '🇧🇩' },
    { country: 'Argélia', percentage: '7%', flag: '🇩🇿' },
    { country: 'Nigéria', percentage: '6%', flag: '🇳🇬' }
  ];

  const processSteps = [
    { step: '1', title: t('sugar.process.step1.title'), description: t('sugar.process.step1.description') },
    { step: '2', title: t('sugar.process.step2.title'), description: t('sugar.process.step2.description') },
    { step: '3', title: t('sugar.process.step3.title'), description: t('sugar.process.step3.description') },
    { step: '4', title: t('sugar.process.step4.title'), description: t('sugar.process.step4.description') }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/a2390eb0e7d872b9a98f0aaa0228d43a.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
              {t('sugar.hero.title')}
            </h1>
            <h2 className="text-4xl md:text-5xl font-light mb-8 text-blue-300">
              {t('sugar.hero.subtitle')}
            </h2>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-2xl"></i>
        </div>
      </section>

      {/* Types Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              {t('sugar.types.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-blue-600 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {sugarTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3 text-blue-600">{type.name}</h3>
                <p className="text-gray-600">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regions Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              {t('sugar.regions.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-blue-600 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {regions.map((region, index) => (
              <div key={index} className="text-center">
                <div className="bg-blue-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">{region.production}</span>
                </div>
                <h3 className="text-xl font-bold mb-2">{region.name}</h3>
                <p className="text-gray-600">{region.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              {t('sugar.process.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-blue-600 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((process, index) => (
              <div key={index} className="text-center">
                <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <span className="font-bold">{process.step}</span>
                </div>
                <h3 className="text-lg font-bold mb-2">{process.title}</h3>
                <p className="text-gray-600 text-sm">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Section */}
      <section className="py-24 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
              {t('sugar.market.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-6">{t('sugar.market.destinations')}</h3>
              <div className="space-y-4">
                {destinations.map((dest, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{dest.flag}</span>
                      <span className="font-medium">{dest.country}</span>
                    </div>
                    <span className="font-bold text-blue-600">{dest.percentage}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold mb-6">{t('sugar.market.factors.title')}</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <i className="ri-weather-cloudy-line text-blue-600 text-xl mt-1"></i>
                  <div>
                    <strong>{t('sugar.market.factors.climate.title')}</strong>
                    <p className="text-gray-600">{t('sugar.market.factors.climate.description')}</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-oil-line text-blue-600 text-xl mt-1"></i>
                  <div>
                    <strong>{t('sugar.market.factors.oil.title')}</strong>
                    <p className="text-gray-600">{t('sugar.market.factors.oil.description')}</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-global-line text-blue-600 text-xl mt-1"></i>
                  <div>
                    <strong>{t('sugar.market.factors.demand.title')}</strong>
                    <p className="text-gray-600">{t('sugar.market.factors.demand.description')}</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-exchange-dollar-line text-blue-600 text-xl mt-1"></i>
                  <div>
                    <strong>{t('sugar.market.factors.exchange.title')}</strong>
                    <p className="text-gray-600">{t('sugar.market.factors.exchange.description')}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Curiosities Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              {t('sugar.curiosities.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-blue-600 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">60%</div>
              <p>{t('sugar.curiosities.exported')}</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">20%</div>
              <p>{t('sugar.curiosities.worldShare')}</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">600</div>
              <p>{t('sugar.curiosities.mills')}</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">8M</div>
              <p>{t('sugar.curiosities.hectares')}</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">1°</div>
              <p>{t('sugar.curiosities.worldRank')}</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">100+</div>
              <p>{t('sugar.curiosities.countries')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div 
              className="h-80 rounded-lg"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Bulk%20carrier%20ship%20being%20loaded%20with%20raw%20sugar%20at%20Brazilian%20export%20port%2C%20modern%20cargo%20loading%20equipment%2C%20industrial%20port%20operations%2C%20sugar%20export%20terminal%2C%20professional%20maritime%20photography%2C%20bright%20daylight%2C%20high%20detail%2C%20clean%20composition&width=600&height=400&seq=sugar-port&orientation=landscape')`,
                backgroundSize: 'cover',
                backgroundPosition: 'object-top'
              }}
            ></div>
            <div 
              className="h-80 rounded-lg"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Large%20sugar%20storage%20silos%20and%20warehouses%20at%20Brazilian%20processing%20facility%2C%20industrial%20sugar%20storage%20infrastructure%2C%20modern%20agricultural%20storage%2C%20sugar%20granules%2C%20professional%20industrial%20photography%2C%20bright%20industrial%20lighting%2C%20high%20detail&width=600&height=400&seq=sugar-storage&orientation=landscape')`,
                backgroundSize: 'cover',
                backgroundPosition: 'object-top'
              }}
            ></div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
